import { useMemo } from 'react';

type SafeAreaInsets = {
  top: number;
  bottom: number;
  left: number;
  right: number;
};

type PaddingResult = {
  bottom: number;
};

/**
 * Centralized hook for all bottom padding logic
 * Handles both safe area insets and keyboard height
 *
 * Logic:
 * 1. If keyboard is showing: use ONLY keyboard height (safe area is hidden behind keyboard)
 * 2. If keyboard is NOT showing: check if Android is handling safe area
 *    - If Android is handling it: return 0 (system already adjusted)
 *    - If not: use safe area bottom inset
 * 3. Return 0 if not React Native
 */
export const useSafeAreaAndKeyboardPadding = (
  isNativeApp: boolean,
  effectiveKeyboardHeight: number,
  isSystemResizing: boolean,
  safeAreaInsets: SafeAreaInsets | undefined,
  screenHeight: number | null
): PaddingResult => {
  return useMemo(() => {
    const safeAreaBottom = safeAreaInsets?.bottom ?? 0;

    // No padding for non-React Native (web)
    if (!isNativeApp) {
      return { bottom: 0 };
    }

    // Check if screen.height differs from window.innerHeight (>1px tolerance)
    // This indicates Android already adjusted window for safe area/keyboard
    const windowHeight = typeof window !== 'undefined' ? window.innerHeight : null;
    const heightDiff =
      screenHeight !== null && windowHeight !== null ? Math.abs(screenHeight - windowHeight) : 0;
    const isAndroidHandling = heightDiff > 1;

    // Debug: Uncomment to see values in console
    // console.warn('PADDING_DEBUG', { screenHeight, windowHeight, heightDiff, isAndroidHandling, effectiveKeyboardHeight });

    // Keyboard is not showing, check if Android is handling safe area
    if (effectiveKeyboardHeight === 0) {
      return { bottom: isAndroidHandling ? 0 : safeAreaBottom };
    }

    // System is resizing window (old Android with adjustResize)
    // Window height already adjusted, don't add padding
    if (isSystemResizing) {
      return { bottom: 0 };
    }

    // If Android is handling the adjustment, don't add keyboard padding
    if (isAndroidHandling) {
      return { bottom: 0 };
    }

    // Normal case: use ONLY keyboard height (safe area hidden behind keyboard)
    return { bottom: effectiveKeyboardHeight };
  }, [isNativeApp, effectiveKeyboardHeight, isSystemResizing, safeAreaInsets?.bottom, screenHeight]);
};
